/**
 * @fileoverview Provide a shared context-action surface whose domain commands remain caller-owned. An imperative ref connects the triggering event to the popup; the component owns viewport placement and dismissal. `AppIcon.js` supplies managed signifiers. Showing a menu does not itself execute or authorize an action.
 */

import React, { useState, useEffect, useImperativeHandle, forwardRef, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import AppIcon from "./AppIcon";

const VIEWPORT_PADDING = 8;

const menuStyle = {
  position: 'fixed',
  backgroundColor: 'var(--surface-card)',
  border: '1px solid rgba(100, 116, 139, 0.24)',
  borderRadius: '12px',
  boxShadow: '0 14px 32px rgba(15, 23, 42, 0.22)',
  zIndex: 4000,
  minWidth: '200px',
  padding: '0.35rem',
  overflow: 'hidden'
};

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  padding: '0.7rem 0.8rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-color)',
  border: '1px solid transparent',
  borderRadius: '10px',
  background: 'transparent',
  width: '100%',
  textAlign: 'left',
  fontFamily: 'inherit',
  transition: 'background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease'
};

const iconStyle = {
  width: '1rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  fontSize: '0.9rem'
};

const labelStyle = {
  flex: 1,
  minWidth: 0
};



const getItemPalette = (item) => {
  if (item.severity === 'danger') {
    return {
      base: {
        backgroundColor: 'rgba(127, 29, 29, 0.08)',
        borderColor: 'rgba(248, 113, 113, 0.24)',
        color: '#fca5a5'
      },
      hover: {
        backgroundColor: 'rgba(127, 29, 29, 0.14)',
        borderColor: 'rgba(248, 113, 113, 0.34)',
        color: '#fee2e2'
      }
    };
  }

  if (item.severity === 'success') {
    return {
      base: {
        backgroundColor: 'rgba(20, 83, 45, 0.08)',
        borderColor: 'rgba(74, 222, 128, 0.2)',
        color: '#86efac'
      },
      hover: {
        backgroundColor: 'rgba(20, 83, 45, 0.14)',
        borderColor: 'rgba(74, 222, 128, 0.3)',
        color: '#dcfce7'
      }
    };
  }

  return {
    base: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: 'var(--text-color)'
    },
    hover: {
      backgroundColor: 'rgba(148, 163, 184, 0.09)',
      borderColor: 'rgba(100, 116, 139, 0.18)',
      color: 'var(--text-color)'
    }
  };
};

/**
 * Call ref.current.toggle(event) from the triggering control. Menu items own their command callbacks; popup visibility and dismissal are local interaction state rather than saved domain state.
 */
const AppMenu = forwardRef(function AppMenu({ model }, ref) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);

  useImperativeHandle(ref, () => ({


    toggle(event) {
      if (visible) {
        setVisible(false);
      } else {
        const rect = event.currentTarget.getBoundingClientRect();
        setPosition({ top: rect.bottom + 4, left: rect.left });
        setVisible(true);
      }
    }
  }));

  useLayoutEffect(

  () => {
    if (!visible || !menuRef.current || typeof window === 'undefined') return;

    const rect = menuRef.current.getBoundingClientRect();
    const nextLeft = Math.min(
      Math.max(position.left, VIEWPORT_PADDING),
      window.innerWidth - rect.width - VIEWPORT_PADDING
    );
    const nextTop = Math.min(
      Math.max(position.top, VIEWPORT_PADDING),
      window.innerHeight - rect.height - VIEWPORT_PADDING
    );

    if (nextLeft !== position.left || nextTop !== position.top) {
      setPosition({ top: nextTop, left: nextLeft });
    }
  }, [visible, position]);

  useEffect(

  () => {
    if (!visible) return;
    const close =

    () => setVisible(false);
    // Delay to avoid closing immediately from the same click
    const timer = setTimeout(() => document.addEventListener('click', close), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', close);
    };
  }, [visible]);

  if (!visible || typeof document === 'undefined') return null;

  const items = model || [];

  return createPortal(
    <div ref={menuRef} style={{ ...menuStyle, top: position.top, left: position.left }}>
            {items.map((item, i) => {
        const palette = getItemPalette(item);

        return (
          <button
            key={i}
            type="button"
            style={{ ...itemStyle, ...palette.base }}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, palette.hover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, palette.base);
            }}
            onClick={() => {
              if (item.command) item.command();
              setVisible(false);
            }}>

                        {item.icon &&
            <span style={iconStyle}>
                                <AppIcon icon={item.icon} />
                            </span>}

                        <span style={labelStyle}>{item.label}</span>
                    </button>);

      })}
        </div>,
    document.body
  );
});

export default AppMenu;
